import { debounce } from "lodash";
import React, { ReactElement, useEffect, useRef, useState } from "react";
import { Point, addPoints } from "../GeometricLayout";
import cssMaxZIndex from "../util/cssMaxZIndex";
import { useAppSelector } from "../store/hooks";
import { editorAdapter } from "../editorsSlice";

type ColorString = string;
export const UNDERLINE_OVERLAY_COLOR: ColorString = "rgba(0,255,177,0.1)";

interface Props {
  editorId: number;
  children?: React.ReactNode;

  /**
   * The color of the background to set to visualuze the overlay.
   */
  debug?: ColorString;

  /**
   * If true, the overlay doesn't match the scrolling of the editor.
   *
   * Useful for positioning elements relative to the visible area of the editor,
   * rather than relative to the (scrollable) inner content.
   */
  frozen?: boolean;

  /**
   * If true, the overlay is at the highest z-index to float above all the other content.
   */
  floating?: boolean;

  /**
   * The element to which this overlay is relative to. Could be the overall document, or
   * a particular part of the page.
   *
   * The default is the document.documentElement
   */
  relativeElement?: HTMLElement;
}

function computePosition(
  editorBoundingClientPoint: Point,
  relativeElement: HTMLElement,
  relativeScroll: Point
) {
  const { top, left } = editorBoundingClientPoint;
  const { top: relativeTop, left: relativeLeft } =
    relativeElement.getBoundingClientRect(); // TODO(perf): cache this somehow?

  return {
    top: top - relativeTop + relativeScroll.top,
    left: left - relativeLeft + relativeScroll.left,
  };
}

function usePosition(editorBound: Point, relativeElement: HTMLElement): Point {
  const [scroll, setScroll] = useState(() => ({
    top: relativeElement.scrollTop,
    left: relativeElement.scrollLeft,
  }));

  const [position, setPosition] = useState(() => {
    return computePosition(editorBound, relativeElement, scroll);
  });

  useEffect(() => {
    const handler = debounce(() => {
      setScroll({
        top: relativeElement.scrollTop,
        left: relativeElement.scrollLeft,
      });
    }, 50);

    relativeElement.addEventListener("scroll", handler);
    return () => {
      relativeElement.removeEventListener("scroll", handler);
    };
  }, [relativeElement]);

  useEffect(() => {
    setPosition(computePosition(editorBound, relativeElement, scroll));
  }, [editorBound, relativeElement, scroll]);
  return position;
}

/**
 * Sits on top (z-index) of an editor to lay out underlines for its visible text.
 *
 * Matches the scrolling offset, style and size of the "content area" for an editor.
 *
 * "Content area" includes the padding, and all the content within an editor. It does not
 * include the border or scrollbar (which are included in the boundingClientRect)
 */
function Overlay({
  editorId,
  children,
  debug,
  frozen,
  floating,
  relativeElement = document.documentElement,
}: Props): ReactElement {
  const editor = useAppSelector((state) =>
    editorAdapter.getSelectors().selectById(state.editors, editorId)
  );
  const position = usePosition(editor!.boundingClientRect, relativeElement);

  const overlayRef = useRef<HTMLDivElement | null>(null);

  // Matches the scroll offsets in the editor
  useEffect(() => {
    if (frozen) {
      return;
    }
    if (!overlayRef.current || !editor) {
      return;
    }
    overlayRef.current.scrollTop = editor.scrollTop;
    overlayRef.current.scrollLeft = editor.scrollLeft;
  }, [frozen, overlayRef, editor]);

  if (!editor) {
    console.error("Couldn't find editor with id", editorId);
    return <></>;
  }

  const { boundingClientRect } = editor;

  const contentTopLeft = addPoints(position, editor.border);
  return (
    <div
      ref={overlayRef}
      style={{
        position: "fixed",
        boxSizing: "content-box",
        ...contentTopLeft,
        width:
          editor.contentArea.width + editor.padding.left + editor.padding.right,
        height:
          editor.contentArea.height +
          editor.padding.top +
          editor.padding.bottom,
        resize: "none",
        pointerEvents: "none",
        background: debug || "transparent",
        overflow: "hidden",
        zIndex: floating ? cssMaxZIndex : 1,
      }}
    >
      <div style={{ position: "relative", top: 0, left: 0 }}>
        <div
          style={{
            height: frozen
              ? boundingClientRect.height -
                editor.border.top -
                editor.border.bottom
              : editor.scrollHeight,
            width: frozen ? boundingClientRect.width : editor.scrollWidth,
          }}
        >
          {children}
        </div>
      </div>
    </div>
  );
}

export default Overlay;
